import { type FC, useState, useMemo, useEffect } from 'react'
import {
    Users,
    Receipt,
    TrendingUp,
    AlertCircle,
    RefreshCw,
} from 'lucide-react'
import { StatsCard } from '@/components/dashboard/StatsCard'
import { StatsCardSkeleton } from '@/components/dashboard/StatsCardSkeleton'
import { DataTable } from '@/components/data-table/DataTable'
import { transactionColumns } from '@/components/dashboard/TransactionColumns'
import { type Transaction } from '@/hooks/useTransactions'
import { TransactionDetailsModal } from '@/components/transactions/TransactionDetailsModal'
import {
    DashboardFilterDialog,
    type DashboardTransactionFilters,
} from '@/components/dashboard/DashboardFilterDialog'
import { useDashboardStats } from '@/hooks'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
// import { SentryTestButton } from '@/components/common/SentryTestButton'

const Dashboard: FC = () => {
    const [selectedTransaction, setSelectedTransaction] =
        useState<Transaction | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false)
    const [filters, setFilters] = useState<DashboardTransactionFilters>({})

    const {
        data: dashboardData,
        isLoading,
        error,
        refetch,
    } = useDashboardStats()

    // Show toast notification on error
    useEffect(() => {
        if (error) {
            toast.error('Failed to load dashboard data', {
                description:
                    error instanceof Error
                        ? error.message
                        : 'An unexpected error occurred',
                action: {
                    label: 'Retry',
                    onClick: () => refetch(),
                },
            })
        }
    }, [error, refetch])

    const handleViewTransaction = (transaction: Transaction) => {
        setSelectedTransaction(transaction)
        setIsModalOpen(true)
    }

    const handleDownloadReceipt = (transactions: Transaction[]) => {
        console.log('Download receipts for:', transactions)
        // Implement download receipt logic
    }

    const handleRetry = () => {
        refetch()
    }

    const handleApplyFilters = (newFilters: DashboardTransactionFilters) => {
        setFilters(newFilters)
    }

    // Transform recent transactions to match the legacy Transaction type
    const recentTransactions = useMemo<Transaction[]>(() => {
        if (!dashboardData?.recentTransactions) return []

        const transactions = dashboardData.recentTransactions.map(txn => ({
            id: txn.id,
            date: new Date(txn.date).toLocaleString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }),
            accountName: txn.accountName,
            walletAddress: txn.walletAddress ?? '',
            txnType: 'Receive' as const,
            amount: parseFloat(txn.cryptoAmount),
            token: txn.cryptocurrency.toUpperCase() as Transaction['token'],
            status: (txn.status === 'COMPLETED'
                ? 'Completed'
                : txn.status === 'PENDING'
                  ? 'Pending'
                  : txn.status === 'CANCELLED'
                    ? 'Cancelled'
                    : 'Failed') as
                | 'Completed'
                | 'Pending'
                | 'Failed'
                | 'Cancelled',
        }))

        // Apply filters
        return transactions.filter(txn => {
            // Search filter
            if (filters.search) {
                const searchLower = filters.search.toLowerCase()
                const matchesSearch =
                    txn.id.toLowerCase().includes(searchLower) ||
                    txn.accountName.toLowerCase().includes(searchLower) ||
                    txn.walletAddress.toLowerCase().includes(searchLower)
                if (!matchesSearch) return false
            }

            // Status filter
            if (filters.status) {
                const statusMatch =
                    txn.status.toUpperCase() === filters.status.toUpperCase()
                if (!statusMatch) return false
            }

            // Cryptocurrency filter
            if (filters.cryptocurrency) {
                if (txn.token !== filters.cryptocurrency) return false
            }

            return true
        })
    }, [dashboardData, filters])

    // Format numbers for display
    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('en-US').format(num)
    }

    const formatCurrency = (num: number) => {
        return new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN',
        }).format(num)
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-center h-[400px]">
                    <div className="text-center space-y-4">
                        <div className="flex justify-center">
                            <div className="rounded-full bg-red-100 p-3">
                                <AlertCircle className="h-6 w-6 text-red-600" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-semibold">
                                Failed to load dashboard data
                            </h3>
                            <p className="text-sm text-muted-foreground max-w-md">
                                {error instanceof Error
                                    ? error.message
                                    : 'An unexpected error occurred while fetching dashboard statistics.'}
                            </p>
                        </div>
                        <Button
                            onClick={handleRetry}
                            variant="outline"
                            className="gap-2"
                        >
                            <RefreshCw className="h-4 w-4" />
                            Retry
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {isLoading ? (
                    <>
                        <StatsCardSkeleton />
                        <StatsCardSkeleton />
                        <StatsCardSkeleton />
                    </>
                ) : (
                    <>
                        <StatsCard
                            title="NO OF USERS"
                            value={formatNumber(
                                dashboardData?.totalUsers?.count || 0
                            )}
                            subtitle={`REGISTERED THIS MONTH: ${formatNumber(dashboardData?.totalUsers?.currentMonth || 0)}`}
                            icon={Users}
                            iconClassName="bg-blue-100 text-blue-600"
                        />
                        <StatsCard
                            title="NO OF TRANSACTIONS"
                            value={formatNumber(
                                dashboardData?.totalTransactionVolume?.count ||
                                    0
                            )}
                            subtitle={`DAILY COUNT: ${formatNumber(dashboardData?.totalTransactionVolume?.dailyCount || 0)}`}
                            icon={Receipt}
                            iconClassName="bg-green-100 text-green-600"
                        />
                        <StatsCard
                            title="TOTAL TRANSACTION VALUE"
                            value={formatCurrency(
                                dashboardData?.totalTransactionValue?.amount ||
                                    0
                            )}
                            subtitle={`DAILY COUNT: ${formatNumber(dashboardData?.totalTransactionValue?.dailyCount || 0)}`}
                            icon={TrendingUp}
                            iconClassName="bg-purple-100 text-purple-600"
                        />
                    </>
                )}
            </div>

            {/* Recent Transactions */}
            <div className="space-y-4">
                <div>
                    <h2 className="text-lg font-semibold">
                        Recent transaction
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        Manage and track all transactions initiated on Ogaboss.
                    </p>
                </div>

                {isLoading ? (
                    <div className="space-y-3">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-[400px] w-full" />
                    </div>
                ) : (
                    <DataTable
                        columns={transactionColumns}
                        data={recentTransactions}
                        searchPlaceholder="Search using transaction ID"
                        onFilter={() => setIsFilterDialogOpen(true)}
                        onViewRow={handleViewTransaction}
                        onDownloadReceipt={handleDownloadReceipt}
                    />
                )}
            </div>

            {/* Transaction Details Modal */}
            <TransactionDetailsModal
                open={isModalOpen}
                onOpenChange={setIsModalOpen}
                transaction={selectedTransaction}
            />

            {/* Filter Dialog */}
            <DashboardFilterDialog
                open={isFilterDialogOpen}
                onOpenChange={setIsFilterDialogOpen}
                currentFilters={filters}
                onApplyFilters={handleApplyFilters}
            />

            {/* Sentry Test Button - Uncomment to test Sentry integration */}
            {/* {import.meta.env.DEV && <SentryTestButton />} */}
        </div>
    )
}

export default Dashboard
